import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

/**
 * CountryNav — nav variant for country microsite pages.
 * Deliberately omits the multi-market link list to reinforce a "dedicated desk"
 * perception without ever asserting exclusivity. The full market grid remains
 * accessible on the global homepage `/`.
 */
export default function CountryNav({ country }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setOpen(false), [loc.pathname]);

  const countryHref = `/${country.code}`;
  const deskLabel = 'International practice';

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
        scrolled ? 'bg-black/70 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
      }`}
      data-testid="country-nav"
    >
      <div className="mx-auto max-w-[1600px] px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <Link to={countryHref} className="flex items-baseline gap-3" data-testid="country-nav-logo">
          <span className="font-serif text-xl md:text-2xl tracking-tight font-light text-white">Belliardo</span>
          <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40 hidden md:inline">
            {deskLabel} · Verona
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          <a
            href="#workflow"
            data-testid="country-nav-workflow"
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white hover-underline"
          >
            Workflow
          </a>
          <Link
            to="/articles"
            data-testid="country-nav-journal"
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white hover-underline"
          >
            Journal
          </Link>
          <a
            href="#claim-form"
            data-testid="country-nav-contact"
            className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/60 hover:text-white hover-underline"
          >
            Contact
          </a>
        </nav>

        <div className="flex items-center gap-3">
          <a
            href="#claim-form"
            data-testid="country-nav-cta"
            className="hidden md:inline-flex items-center gap-2 px-4 py-2 border border-white/20 text-white text-[11px] font-mono uppercase tracking-[0.2em] hover:bg-white hover:text-black transition-colors duration-300"
          >
            Submit a Claim
          </a>
          <button
            aria-label="Toggle menu"
            data-testid="country-nav-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" strokeWidth={1.25} /> : <Menu className="h-5 w-5" strokeWidth={1.25} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              <a href="#workflow" className="font-mono text-xs uppercase tracking-[0.25em] text-white/80">Workflow</a>
              <Link to="/articles" className="font-mono text-xs uppercase tracking-[0.25em] text-white/80">Journal</Link>
              <a href="#claim-form" className="font-mono text-xs uppercase tracking-[0.25em] text-white/80">Contact</a>
              <a
                href="#claim-form"
                className="mt-2 inline-flex items-center gap-2 px-4 py-3 border border-white/20 text-white text-[11px] font-mono uppercase tracking-[0.2em] w-fit"
              >
                Submit a Claim
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
